import { useContext } from 'react';

import workTypeIconRegularSilver from '@atom/components/common/svgIcons/workTypeIconRegularSilver.svg';
import WorkTemplateTree from '@atom/components/common/workTemplateTree/WorkTemplateTree';
import { Button, Icon, RadioGroup } from '@atom/mui';
import { AddIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { PolicyAction } from '@atom/types/policy';
import { WorkOrderType } from '@atom/types/work';
import { generateYYYYMMName } from '@atom/utilities/timeUtilities';

import CreateWorkModalContext, {
  initialDraft,
  ModalStep,
  WorkCreateType,
} from '../CreateWorkModalContext';

import classes from './steps.module.css';

const { RadioButton } = RadioGroup;

const WorkTemplateStep = () => {
  const {
    draft,
    setActiveStep,
    setDraft,
    workTemplateRequired,
    showModeToggle,
    assetLocked,
    onCreateWorkTemplate,
    onTemplateClick,
  } = useContext(CreateWorkModalContext);

  const isBulk = draft.mode === 'BULK';

  const handleModeChange = (mode: WorkCreateType) => {
    setDraft(prev => ({
      ...initialDraft,
      mode,
      dueDate: prev.dueDate,
      fromCalendarDate: prev.fromCalendarDate,
      fromCalendarGap: prev.fromCalendarGap,
    }));
  };

  const handleStartFromScratch = () => {
    setDraft(prev => ({
      ...prev,
      workTemplate: null,
      workTemplateType: WorkOrderType.DEFAULT,
      name: generateYYYYMMName(null),
      ...(assetLocked
        ? {}
        : { asset: null, assetId: null, isLocationBased: false }),
      fields: [],
      fieldOrder: [],
      summary: [],
      tasks: [],
      startedFromScratch: true,
    }));

    setActiveStep(ModalStep.DETAILS);
  };

  const emptyContent = (
    <div className={classes.emptyTemplates}>
      <img
        src={workTypeIconRegularSilver}
        alt=""
        className={classes.emptyTemplatesIcon}
      />
      <div className={classes.emptyTemplatesTitle}>No Work Template Found</div>
      <div className={classes.emptyTemplatesBody}>
        Work templates make it easy to standardize work, so you can quickly
        create repeatable workflows without starting from scratch.
      </div>
      <Button
        variant="contained"
        color="primary"
        onClick={onCreateWorkTemplate}
        data-cy="createWorkTemplateCta"
      >
        Create Work Template
      </Button>
    </div>
  );

  return (
    <div className={classes.templateStep}>
      {showModeToggle && (
        <RadioGroup
          row
          value={draft.mode}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            handleModeChange(event.target.value as WorkCreateType)
          }
        >
          <RadioButton value="SINGLE" label="Single" />
          <RadioButton value="BULK" label="Multiple" />
        </RadioGroup>
      )}
      <div className={classes.templateSection}>
        <div className={classes.templateHeading}>
          {isBulk
            ? 'Choose a work template that is not linked to an asset type:'
            : 'Choose a work template:'}
        </div>
        <div className={classes.templateTree}>
          <WorkTemplateTree
            includeSearch
            fixedSearchbar
            hideSearchOnEmpty
            autoFocus={workTemplateRequired}
            action={PolicyAction.CREATE}
            schemaId={assetLocked ? draft.asset?.schemaId : null}
            hasSchema={isBulk || assetLocked ? false : null}
            type={assetLocked ? WorkOrderType.DEFAULT : null}
            appointmentsEnabled={isBulk ? false : null}
            onClick={onTemplateClick}
            select="highlight"
            getTemplateSelected={template =>
              template.id === draft.workTemplate?.id
            }
            emptyContent={emptyContent}
          />
        </div>
      </div>
      {!workTemplateRequired && !isBulk && (
        <div className={classes.scratchAction}>
          <div className={classes.scratchLabel}>Or start from scratch:</div>
          <Button
            fullWidth
            onClick={handleStartFromScratch}
            startIcon={<Icon icon={AddIcon} color={colors.neutral.dim} />}
            data-cy="createWorkWithoutTemplateButton"
            sx={{
              justifyContent: 'flex-start',
              textTransform: 'none',
              color: colors.neutral.dim,
              border: `1px dashed ${colors.neutral.silver}`,
              borderRadius: '0.25rem',
              padding: '0.375rem 1rem',
            }}
          >
            Create work without a template
          </Button>
        </div>
      )}
    </div>
  );
};

export default WorkTemplateStep;
